import React, { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, ShieldCheck, Sparkles, HeartHandshake, Award } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { useAssets } from '../assets/AssetsContext';

/**
 * Hero with a "cinematic scene" rotator.
 * Each scene is a slow Ken-Burns slideshow of curated images representing the
 * city and its patient-result vibe. A button lets the visitor switch scenes.
 *
 * Two scenes available:
 *   1. Istanbul  - Bosphorus, Galata, patient smiles, body-contouring results
 *   2. Casablanca - Hassan II mosque, Moroccan coastline, smiles, results
 *
 * The dark-teal overlay is preserved so brand palette stays identical.
 *
 * NOTE: To use real video footage instead of the slideshow, upload an MP4
 * per scene and replace each scene.frames[] with a single <video> source.
 */
const scenes = [
  {
    id: 'istanbul',
    city: 'Istanbul',
    country: 'Turkey',
    flag: '\u{1F1F9}\u{1F1F7}',
    frames: [
      'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1920&q=85', // Galata tower
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1920&q=85', // Woman confident smile
      'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=1920&q=85', // Bosphorus aerial
      'https://images.unsplash.com/photo-1549445069-d1125f7a129c?w=1920&q=85',    // Toned body contour
      'https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?w=1920&q=85', // Istanbul sunset
      'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=1920&q=85', // Bright smile (teeth)
    ],
  },
  {
    id: 'casablanca',
    city: 'Casablanca',
    country: 'Morocco',
    flag: '\u{1F1F2}\u{1F1E6}',
    frames: [
      'https://images.unsplash.com/photo-1552693673-1bf958298935?w=1920&q=85', // Face aesthetic portrait
      'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=1920&q=85', // Smiling woman portrait
      'https://images.unsplash.com/photo-1539020140153-e479b8c22e70?w=1920&q=85', // Moroccan tiles
      'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=1920&q=85', // Beauty portrait
      'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?w=1920&q=85', // Bright teeth smile
      'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=1920&q=85', // Beauty close-up
    ],
  },
];

const FRAME_MS = 3800;

const Hero = () => {
  const { t } = useLang();
  const { overrides, apiBase } = useAssets();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [frameIdx, setFrameIdx] = useState(0);
  const baseScene = scenes[sceneIdx];
  // Apply per-frame overrides if uploaded.
  const scene = {
    ...baseScene,
    frames: baseScene.frames.map((src, i) => {
      const ov = overrides[`hero.${baseScene.id}.${i}`];
      return ov ? `${apiBase}${ov.url}` : src;
    }),
  };

  // Optional hero background video — if uploaded, takes precedence over the slideshow.
  const heroVideo = overrides['hero.video'];
  const heroVideoUrl = heroVideo ? `${apiBase}${heroVideo.url}` : null;
  const heroVideoMime = heroVideo?.mime || 'video/mp4';

  // auto-advance frames within the active scene
  useEffect(() => {
    if (heroVideoUrl) return; // video plays continuously, no slideshow rotation
    const t = setInterval(() => {
      setFrameIdx((i) => (i + 1) % scene.frames.length);
    }, FRAME_MS);
    return () => clearInterval(t);
  }, [scene, heroVideoUrl]);

  // reset frame when scene changes
  useEffect(() => { setFrameIdx(0); }, [sceneIdx]);

  const nextScene = () => setSceneIdx((i) => (i + 1) % scenes.length);
  const prevScene = () => setSceneIdx((i) => (i - 1 + scenes.length) % scenes.length);

  return (
    <section id="home" className="relative overflow-hidden bg-[#0B3D2E] min-h-[90vh] flex items-center">
      {/* Background — either an uploaded video or the cross-fading slideshow */}
      <div className="absolute inset-0 z-0">
        {heroVideoUrl ? (
          <video
            key={heroVideoUrl}
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={scene.frames[0]}
            data-testid="hero-video"
          >
            <source src={heroVideoUrl} type={heroVideoMime} />
          </video>
        ) : (
          scene.frames.map((src, i) => (
            <div
              key={`${scene.id}-${i}`}
              className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
              style={{ opacity: i === frameIdx ? 1 : 0 }}
            >
              <img
                src={src}
                alt=""
                className={`w-full h-full object-cover ${i === frameIdx ? 'kenburns-active' : ''}`}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))
        )}
      </div>

      {/* Teal brand tint + vignette (keeps colors identical to the rest of the site) */}
      <div className="absolute inset-0 z-10 pointer-events-none bg-[#0B3D2E]/40" />
      <div className="absolute inset-0 z-10 pointer-events-none hero-grid-bg" />
      <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-[#0B3D2E]/40 via-transparent to-[#0B3D2E]/85" />

      {/* Scene toggle buttons (right side) */}
      <button
        onClick={prevScene}
        aria-label="Previous scene"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-[#0B3D2E]/60 backdrop-blur border border-[#D4AF37]/30 items-center justify-center text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-all"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={nextScene}
        aria-label="Next scene"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-[#0B3D2E]/60 backdrop-blur border border-[#D4AF37]/30 items-center justify-center text-white hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-all"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Content */}
      <div className="relative z-20 max-w-[1440px] mx-auto px-6 pt-24 pb-24 md:pt-32 md:pb-32 flex flex-col items-center text-center w-full">
        <div className="relative max-w-5xl fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs tracking-[0.22em] uppercase mb-8 bg-[#0B3D2E]/40 backdrop-blur-sm">
            <MapPin className="w-3 h-3" />
            <span className="text-sm">{scene.flag}</span>
            {scene.city}, {scene.country}
          </div>

          <h1 className="font-serif-display text-[42px] sm:text-5xl md:text-7xl lg:text-[96px] leading-[1.04] text-white mb-7 drop-shadow-[0_4px_28px_rgba(0,0,0,0.5)] tracking-tight">
            {t('hero.title')}
          </h1>

          <p className="text-white/85 text-base sm:text-lg md:text-[22px] max-w-3xl mx-auto mb-3 leading-relaxed font-light">
            {t('hero.sub')}
          </p>

          {/* Urgency micro-line */}
          <div className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-[#D4AF37] mb-9 mt-2">
            {t('hero.urgency')}
          </div>

          <a
            href="#contact-form"
            data-testid="hero-primary-cta"
            className="btn-gold inline-flex items-center gap-3 px-9 sm:px-12 py-4 sm:py-5 rounded-full text-sm sm:text-base group shadow-[0_18px_40px_-12px_rgba(212,175,55,0.55)]"
          >
            {t('cta.getDiagnosis')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>

          {/* Trust signal strip */}
          <div className="mt-12 sm:mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 max-w-4xl mx-auto" data-testid="hero-trust-strip">
            {[
              { Icon: ShieldCheck, key: 'trust.0' },
              { Icon: HeartHandshake, key: 'trust.1' },
              { Icon: Sparkles, key: 'trust.2' },
              { Icon: Award, key: 'trust.3' },
            ].map(({ Icon, key }) => (
              <div key={key} className="flex items-center gap-3 text-start text-white/85">
                <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/35">
                  <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </span>
                <span className="text-[12px] sm:text-[13px] leading-tight">{t(key)}</span>
              </div>
            ))}
          </div>

          {/* Numerical proof row */}
          <div className="mt-12 sm:mt-14 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-x-10 sm:gap-x-14 gap-y-6">
            {[
              { num: '100K+', label: 'Happy Patients' },
              { num: '80+', label: 'Countries' },
              { num: '15+', label: 'Years Experience' },
              { num: 'ISO', label: 'Certified' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-serif-display text-3xl md:text-4xl gold-text">{s.num}</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-white/70 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Scene dots */}
          <div className="flex items-center justify-center gap-3 mt-10">
            {scenes.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setSceneIdx(i)}
                aria-label={`Show ${s.city} scene`}
                className={`group flex items-center gap-2 transition-all ${
                  i === sceneIdx ? 'text-[#D4AF37]' : 'text-white/55 hover:text-white'
                }`}
              >
                <span
                  className={`h-2 rounded-full transition-all ${
                    i === sceneIdx ? 'w-10 bg-[#D4AF37]' : 'w-2 bg-white/40'
                  }`}
                />
                <span className="text-xs tracking-[0.18em] uppercase">
                  {s.flag} {s.city}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
